<script lang="ts" setup>
import { Loader } from '@googlemaps/js-api-loader'
import type { UserLocation } from '~/types'

defineOptions({
  inheritAttrs: false,
})
const emits = defineEmits(['update:model-value'])
const { googleMapsApiKey } = useRuntimeConfig().public

const props = defineProps<{ modelValue: { lat?: number, lng?: number, name: string, place_id: string } }>()
const currentLocation = ref<UserLocation>({ lat: 0, lng: 0, name: '', place_id: '' })
const autoCompleteService = ref()
const geocoderService = ref()

onMounted(async () => {
  const loader = new Loader({ apiKey: googleMapsApiKey })
  const [{ AutocompleteService }, { Geocoder }] = await Promise.all([loader.importLibrary('places'), loader.importLibrary('geocoding')])
  autoCompleteService.value = new AutocompleteService()
  geocoderService.value = new Geocoder()
  currentLocation.value = { lat: 57.14999930556513, lng: -2.0939238501839132, name: '', place_id: undefined, ...props.modelValue }
})

const loading = ref(false)
const searchQuery = ref('')
const searchResults = ref<{ description: string, place_id: string }[]>([])
const resultsVisible = ref(false)

watch(searchQuery, (val) => {
  if (!val) {
    resultsVisible.value = false
  }
})

watchDebounced(searchQuery, async (val) => {
  if (!val)
    return searchResults.value = []
  loading.value = true
  const found = await autoCompleteService.value.getPlacePredictions({
    input: val, locationBias: {
      center: { lat: 57.047966457756836, lng: -2.492512855583779 },
      radius: 5000,
    },
  })
  searchResults.value = found?.predictions?.length ? found.predictions : []
  resultsVisible.value = true
  loading.value = false
}, { debounce: 500, maxWait: 1000 })

async function selectPlace(result: { description: string, place_id: string }) {
  const info = await geocoderService.value.geocode({ placeId: result.place_id })
  currentLocation.value = { lat: info?.results?.[0]?.geometry?.location?.lat(), lng: info?.results?.[0]?.geometry?.location?.lng(), name: result.description, place_id: result.place_id }
  searchResults.value = []
  resultsVisible.value = false
  searchQuery.value = result.description
  emits('update:model-value', currentLocation.value)
}
const searchbox = ref()

function startSearch() {
  resultsVisible.value = true
  nextTick(() => {
    searchbox.value?.input.focus()
  })
}

const searchcontainer = ref()

onClickOutside(searchcontainer, () => {
  resultsVisible.value = false
  searchQuery.value = ''
})

function removeLocation() {
  currentLocation.value = { lat: undefined, lng: undefined, name: '', place_id: '' }
  resultsVisible.value = false
  emits('update:model-value', currentLocation.value)
}
</script>

<template>
  <div
    ref="searchcontainer"
    class="input-field relative flex items-stretch flex-col flex-1"
  >
    <UInput
      v-if="!resultsVisible"
      :model-value="currentLocation.name"
      class="h-full"
      v-bind="$attrs"
      autocomplete="off"
      :loading="loading"
      name="locationSearch"
      :ui="{ icon: { trailing: { pointer: '' } }, ...$attrs.ui }"
      @click.stop="startSearch"
      @focus="startSearch"
    >
      <template #trailing>
        <UButton
          v-show="currentLocation.name"
          icon="ri:close-line"
          aria-label="Clear Search"
          color="gray"
          variant="ghost"
          size="xs"
          class="z-40 relative hover:text-red-500"
          @click.stop="removeLocation"
        />
      </template>
    </UInput>
    <UInput
      v-else
      ref="searchbox"
      v-model="searchQuery"
      :loading="loading"
      icon="heroicons:magnifying-glass-20-solid"
      v-bind="$attrs"
    />

    <div
      v-show="resultsVisible"
      class="p-3 rounded-lg shadow mt-0 lg:mt-2 bg-slate-50 absolute top-[100%] z-[999] w-full"
    >
      <transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="searchResults?.length"
          class="flex flex-col space-y-1 items-start"
        >
          <UButton
            v-for="result in searchResults"
            :key="result.place_id"
            variant="link"
            color="gray"
            class="text-left"
            @click="selectPlace(result)"
          >
            {{ result.description }}
          </UButton>
        </div>

        <div v-else>
          No Results Found
        </div>
      </transition>
    </div>
  </div>
</template>
